import { type ReactElement, type ReactNode, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { HeroHeader } from "@/components/ui/hero-section-1"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { cn } from "@/lib/utils"
import { CalendarClock, Mail, MapPin, Phone, Sparkles, Users, ArrowRight, CheckCircle2 } from "lucide-react"

type AppointmentForm = {
  name: string
  company: string
  email: string
  location: string
  headcount: string
  preferredDate: string
  preferredTime: string
  notes: string
}

type MessageForm = {
  name: string
  email: string
  subject: string
  message: string
}

const defaultAppointmentForm: AppointmentForm = {
  name: "",
  company: "",
  email: "",
  location: "",
  headcount: "",
  preferredDate: "",
  preferredTime: "",
  notes: "",
}

const defaultMessageForm: MessageForm = {
  name: "",
  email: "",
  subject: "",
  message: "",
}

const heroHighlights = [
  {
    icon: CalendarClock,
    title: "Go live in 2 hours",
    description: "We preload menus, payments, and staff roles so launch day is turnkey.",
  },
  {
    icon: Users,
    title: "Built for nimble teams",
    description: "Handle pop-ups, cafés, ghost kitchens, or fleets with one control tower.",
  },
  {
    icon: Sparkles,
    title: "AI + ops copilots",
    description: "Inventory, payroll, rush-mode UI, and autopilot alerts bundled in.",
  },
] as const

const TalkToBraisePage = (): ReactElement => {
  const [appointmentForm, setAppointmentForm] = useState<AppointmentForm>(defaultAppointmentForm)
  const [messageForm, setMessageForm] = useState<MessageForm>(defaultMessageForm)
  const [appointmentSubmitting, setAppointmentSubmitting] = useState(false)
  const [messageSubmitting, setMessageSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState("appointment")
  const { toast } = useToast()

  const contactEmail = "hello@braisepos.com"
  const calendlyUrl = "https://cal.com/braise/consult"

  const appointmentValid = useMemo(() => {
    return (
      appointmentForm.name.trim().length > 1 &&
      appointmentForm.company.trim().length > 1 &&
      appointmentForm.email.trim().length > 3 &&
      appointmentForm.location.trim().length > 1 &&
      appointmentForm.preferredDate.trim().length > 0 &&
      appointmentForm.preferredTime.trim().length > 0
    )
  }, [appointmentForm])

  const messageValid = useMemo(() => {
    return (
      messageForm.name.trim().length > 1 &&
      messageForm.email.trim().length > 3 &&
      messageForm.subject.trim().length > 1 &&
      messageForm.message.trim().length > 10
    )
  }, [messageForm])

  const resolveContextPath = () => {
    if (typeof window === "undefined") {
      return "/talk-to-braise"
    }
    const { pathname, search } = window.location
    const combined = `${pathname}${search}`
    return combined.length > 0 ? combined : "/talk-to-braise"
  }

  const submitInquiry = async (endpoint: string, payload: Record<string, unknown>) => {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    })

    let json: { ok?: boolean; message?: string } | null = null
    try {
      json = await response.json()
    } catch {
      json = null
    }

    if (!response.ok || !json?.ok) {
      const fallback = typeof json?.message === "string" ? json.message : "Please try again in a moment."
      throw new Error(fallback)
    }

    return json
  }

  const handleAppointmentSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!appointmentValid) {
      toast({ title: "Missing details", description: "Please fill every required field.", variant: "destructive" })
      return
    }

    setAppointmentSubmitting(true)
    try {
      await submitInquiry("/api/public/talk-to-braise/appointment", {
        ...appointmentForm,
        headcount: appointmentForm.headcount || undefined,
        notes: appointmentForm.notes || undefined,
        source: "talk-to-braise",
        contextPath: resolveContextPath(),
      })
      setAppointmentForm({ ...defaultAppointmentForm })
      toast({ title: "Thanks for reaching out", description: "We’ll confirm your demo details shortly." })
    } catch (error) {
      toast({
        title: "Submission failed",
        description:
          error instanceof Error ? error.message : "We couldn’t submit your request. Please try again in a moment.",
        variant: "destructive",
      })
    } finally {
      setAppointmentSubmitting(false)
    }
  }

  const handleMessageSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!messageValid) {
      toast({ title: "Missing details", description: "Please complete the required fields.", variant: "destructive" })
      return
    }

    setMessageSubmitting(true)
    try {
      await submitInquiry("/api/public/talk-to-braise/message", {
        ...messageForm,
        source: "talk-to-braise",
        contextPath: resolveContextPath(),
      })
      setMessageForm({ ...defaultMessageForm })
      toast({ title: "Message received", description: "We’ll reply within one business day." })
    } catch (error) {
      toast({
        title: "Submission failed",
        description:
          error instanceof Error ? error.message : "We couldn’t submit your message. Please try again in a moment.",
        variant: "destructive",
      })
    } finally {
      setMessageSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-blue-100 selection:text-blue-900 flex flex-col lg:flex-row">
      {/* Left Column - Info & Context */}
      <div className="lg:w-1/2 bg-slate-50 border-r border-slate-200 lg:fixed lg:inset-y-0 lg:left-0 overflow-y-auto">
        <div className="p-8 lg:p-16 flex flex-col min-h-full">
          <div className="mb-12">
            <Link to="/" className="inline-flex items-center gap-2 font-bold text-xl tracking-tight text-slate-900 hover:opacity-80 transition-opacity">
              <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center text-white">
                <Sparkles className="h-5 w-5" />
              </div>
              Braise
            </Link>
          </div>

          <div className="max-w-xl">
            <Badge className="mb-6 rounded-full bg-blue-100 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 border border-blue-200 shadow-sm">
              Talk to Sales
            </Badge>
            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl mb-6 leading-tight">
              Ready to modernize your <span className="text-blue-600">operations?</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-12">
              Share a few details about your venues and we’ll tailor a walkthrough. Our hospitality architects will show you exactly how Braise fits your flow.
            </p>

            <div className="space-y-6 mb-16">
              {heroHighlights.map((item) => (
                <div key={item.title} className="flex gap-4 items-start group">
                  <div className="flex-shrink-0 h-12 w-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-blue-600 shadow-sm group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg mb-1">{item.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-auto pt-8 border-t border-slate-200">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-6">Contact Us Directly</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-500">Phone</div>
                    <a href="tel:+18882372473" className="text-sm font-bold text-slate-900 hover:text-blue-600 transition-colors">+1 (888) 237-2473</a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-500">Email</div>
                    <a href={`mailto:${contactEmail}`} className="text-sm font-bold text-slate-900 hover:text-blue-600 transition-colors">{contactEmail}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Form */}
      <div className="lg:w-1/2 lg:ml-auto bg-white min-h-screen">
        <div className="p-8 lg:p-16 max-w-2xl mx-auto flex flex-col justify-center min-h-full">

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-10">
              <TabsList className="bg-slate-100 p-1.5 rounded-full border border-slate-200 relative">
                <TabsTrigger
                  value="appointment"
                  className="rounded-full px-6 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 data-[state=active]:text-blue-700 transition-colors relative z-10 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  {activeTab === "appointment" && (
                    <motion.div
                      layoutId="active-tab-contact"
                      className="absolute inset-0 bg-white rounded-full shadow-md border border-slate-100/50 -z-10"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  Book a Demo
                </TabsTrigger>
                <TabsTrigger
                  value="email"
                  className="rounded-full px-6 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 data-[state=active]:text-blue-700 transition-colors relative z-10 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  {activeTab === "email" && (
                    <motion.div
                      layoutId="active-tab-contact"
                      className="absolute inset-0 bg-white rounded-full shadow-md border border-slate-100/50 -z-10"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  Send a Message
                </TabsTrigger>
              </TabsList>
            </div>

            <AnimatePresence mode="wait">
              <TabsContent value="appointment" className="mt-0 focus-visible:outline-none">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Schedule a working session</h2>
                    <p className="text-slate-600">We'll confirm with a calendar invite shortly.</p>
                  </div>

                  <form onSubmit={handleAppointmentSubmit} className="space-y-6">
                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field label="Full name" required>
                        <Input
                          required
                          value={appointmentForm.name}
                          onChange={(e) => setAppointmentForm(prev => ({ ...prev, name: e.target.value }))}
                          className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all"
                          placeholder="Jane Doe"
                        />
                      </Field>
                      <Field label="Company" required>
                        <Input
                          required
                          value={appointmentForm.company}
                          onChange={(e) => setAppointmentForm(prev => ({ ...prev, company: e.target.value }))}
                          className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all"
                          placeholder="Acme Hospitality"
                        />
                      </Field>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field label="Work email" required>
                        <Input
                          type="email"
                          required
                          value={appointmentForm.email}
                          onChange={(e) => setAppointmentForm(prev => ({ ...prev, email: e.target.value }))}
                          className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all"
                          placeholder="jane@acme.com"
                        />
                      </Field>
                      <Field label="Location" required>
                        <Input
                          required
                          value={appointmentForm.location}
                          onChange={(e) => setAppointmentForm(prev => ({ ...prev, location: e.target.value }))}
                          className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all"
                          placeholder="Los Angeles, CA"
                        />
                      </Field>
                    </div>

                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field label="Headcount/Venues">
                        <Input
                          value={appointmentForm.headcount}
                          onChange={(e) => setAppointmentForm(prev => ({ ...prev, headcount: e.target.value }))}
                          className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all"
                          placeholder="e.g. 3 locations"
                        />
                      </Field>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Date" required>
                          <Input
                            type="date"
                            required
                            value={appointmentForm.preferredDate}
                            onChange={(e) => setAppointmentForm(prev => ({ ...prev, preferredDate: e.target.value }))}
                            className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all px-3"
                          />
                        </Field>
                        <Field label="Time" required>
                          <Input
                            type="time"
                            required
                            value={appointmentForm.preferredTime}
                            onChange={(e) => setAppointmentForm(prev => ({ ...prev, preferredTime: e.target.value }))}
                            className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all px-3"
                          />
                        </Field>
                      </div>
                    </div>

                    <Field label="Anything specific you'd like to see?">
                      <Textarea
                        rows={4}
                        value={appointmentForm.notes}
                        onChange={(e) => setAppointmentForm(prev => ({ ...prev, notes: e.target.value }))}
                        className="min-h-[120px] rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all resize-none"
                        placeholder="Tell us about your goals, pain points, or specific features you're interested in."
                      />
                    </Field>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={!appointmentValid || appointmentSubmitting}
                        className="w-full h-14 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-lg shadow-blue-500/20 transition-all hover:scale-[1.02]"
                      >
                        {appointmentSubmitting ? "Booking..." : "Confirm Booking"}
                      </Button>
                      <div className="mt-4 text-center">
                        <a href={calendlyUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors inline-flex items-center gap-1">
                          Prefer to use a booking link? <ArrowRight className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  </form>
                </motion.div>
              </TabsContent>

              <TabsContent value="email" className="mt-0 focus-visible:outline-none">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Send us a message</h2>
                    <p className="text-slate-600">We route every message to the right team.</p>
                  </div>

                  <form onSubmit={handleMessageSubmit} className="space-y-6">
                    <Field label="Full name" required>
                      <Input
                        required
                        value={messageForm.name}
                        onChange={(e) => setMessageForm(prev => ({ ...prev, name: e.target.value }))}
                        className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all"
                        placeholder="Jane Doe"
                      />
                    </Field>

                    <Field label="Work email" required>
                      <Input
                        type="email"
                        required
                        value={messageForm.email}
                        onChange={(e) => setMessageForm(prev => ({ ...prev, email: e.target.value }))}
                        className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all"
                        placeholder="jane@acme.com"
                      />
                    </Field>

                    <Field label="Subject" required>
                      <Input
                        required
                        value={messageForm.subject}
                        onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e.target.value }))}
                        className="h-12 rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all"
                        placeholder="Partnership inquiry"
                      />
                    </Field>

                    <Field label="Message" required>
                      <Textarea
                        rows={6}
                        required
                        value={messageForm.message}
                        onChange={(e) => setMessageForm(prev => ({ ...prev, message: e.target.value }))}
                        className="min-h-[160px] rounded-xl border-slate-200 bg-slate-50 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-all resize-none"
                        placeholder="How can we help you?"
                      />
                    </Field>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={!messageValid || messageSubmitting}
                        className="w-full h-14 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-lg shadow-lg shadow-slate-900/20 transition-all hover:scale-[1.02]"
                      >
                        {messageSubmitting ? "Sending..." : "Send Message"}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              </TabsContent>
            </AnimatePresence>
          </Tabs>
        </div>
      </div>
    </div>
  )
}

const Field = ({
  children,
  label,
  required,
}: {
  children: ReactElement
  label: string
  required?: boolean
}) => (
  <div className="space-y-2">
    <Label className="text-sm font-semibold text-slate-700">
      {label}
      {required && <span className="ml-1 text-blue-600">*</span>}
    </Label>
    {children}
  </div>
)

export default TalkToBraisePage
